import { Component, OnInit } from '@angular/core';



/* Start Training Category Model Section */
interface EcbTrainingCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  colorClass: string;
  route: string;
}
/* End Training Category Model Section */


/* Start Featured Course Model Section */
interface EcbFeaturedCourse {
  id: number;
  courseCode: string;
  title: string;
  duration: string;
  deliveryMethod: string;
  description: string;
  image: string;
  route: string;
}
/* End Featured Course Model Section */


/* Start Training Process Model Section */
interface EcbTrainingProcess {
  id: number;
  stepNumber: number;
  title: string;
  description: string;
  icon: string;
  colorClass: string;
}
/* End Training Process Model Section */


/* Start Training Benefit Model Section */
interface EcbTrainingBenefit {
  id: number;
  title: string;
}
/* End Training Benefit Model Section */


/* Start Student Testimonial Model Section */
interface EcbStudentTestimonial {
  id: number;
  studentName: string;
  courseName: string;
  message: string;
  rating: number;
  image: string;
}
/* End Student Testimonial Model Section */


@Component({
  selector: 'app-training',
  templateUrl: './training.component.html',
  styleUrl: './training.component.scss'
})
export class TrainingComponent implements OnInit {


  /* Start Property Declaration Section */
  public trainingCategories: EcbTrainingCategory[] = [];
  public featuredCourses: EcbFeaturedCourse[] = [];
  public trainingProcesses: EcbTrainingProcess[] = [];
  public trainingBenefits: EcbTrainingBenefit[] = [];
  public studentTestimonials: EcbStudentTestimonial[] = [];

  public selectedTestimonialPage: number = 0;
  public testimonialItemsPerPage: number = 3;
  /* End Property Declaration Section */


  /* Start Constructor Section */
  constructor() {

  }
  /* End Constructor Section */


  /* Start Component Initialization Section */
  ngOnInit(): void {
    this.getTrainingCategories();
    this.getFeaturedCourses();
    this.getTrainingProcesses();
    this.getTrainingBenefits();
    this.getStudentTestimonials();
  }
  /* End Component Initialization Section */


  /* Start Training Category Static List Section */
  getTrainingCategories(): void {
    this.trainingCategories = [
      {
        id: 1,
        name: 'First Aid',
        description: 'Essential first aid skills for everyday emergencies.',
        icon: 'bi bi-heart-pulse-fill',
        colorClass: 'ecb-web-training-category-orange',
        route: '#featured-courses'
      },
      {
        id: 2,
        name: 'CPR',
        description: 'Learn CPR techniques and save lives with confidence.',
        icon: 'bi bi-prescription2',
        colorClass: 'ecb-web-training-category-green',
        route: '#featured-courses'
      },
      {
        id: 3,
        name: 'Safety',
        description: 'Workplace safety training for a safer environment.',
        icon: 'bi bi-people-fill',
        colorClass: 'ecb-web-training-category-blue',
        route: '#featured-courses'
      },
      {
        id: 4,
        name: 'Fire Safety',
        description: 'Fire awareness and emergency response training.',
        icon: 'bi bi-fire',
        colorClass: 'ecb-web-training-category-red',
        route: '#featured-courses'
      },
      {
        id: 5,
        name: 'Childcare & Education',
        description: 'Courses for educators and childcare professionals.',
        icon: 'bi bi-person-hearts',
        colorClass: 'ecb-web-training-category-purple',
        route: '#featured-courses'
      },
      {
        id: 6,
        name: 'Construction',
        description: 'White card and construction safety courses.',
        icon: 'bi bi-cone-striped',
        colorClass: 'ecb-web-training-category-teal',
        route: '#featured-courses'
      }
    ];
  }
  /* End Training Category Static List Section */


  /* Start Featured Course Static List Section */
  getFeaturedCourses(): void {
    this.featuredCourses = [
      {
        id: 1,
        courseCode: 'HLTAID011',
        title: 'Provide First Aid',
        duration: '1 Day',
        deliveryMethod: 'Face to Face',
        description:
          'Learn how to provide first aid response to a casualty until professional help arrives.',
        image: 'assets/images/training/training_CH30121.png',
        route: '#'
      },
      {
        id: 2,
        courseCode: 'HLTAID009',
        title: 'Provide Cardiopulmonary Resuscitation (CPR)',
        duration: '4 Hours',
        deliveryMethod: 'Face to Face',
        description:
          'Learn CPR techniques to confidently manage cardiac emergencies.',
        image: 'assets/images/training/training_CP001.png',
        route: '#'
      },
      {
        id: 3,
        courseCode: 'CPCCWHS1001',
        title: 'Prepare to Work Safely in the Construction Industry',
        duration: '6 Hours',
        deliveryMethod: 'Face to Face / Online',
        description:
          'Mandatory White Card course for construction industry workers.',
        image: 'assets/images/training/training_HL009.png',
        route: '#'
      },
      {
        id: 4,
        courseCode: 'CHC30121',
        title: 'Certificate III in Early Childhood Education and Care',
        duration: '12 Months',
        deliveryMethod: 'Blended',
        description:
          'Start your career in early childhood education and care.',
        image: 'assets/images/training/training_HL0011.png',
        route: '#'
      }
    ];
  }
  /* End Featured Course Static List Section */


  /* Start Training Process Static List Section */
  getTrainingProcesses(): void {
    this.trainingProcesses = [
      {
        id: 1,
        stepNumber: 1,
        title: 'Choose a Course',
        description: 'Select the course that suits your needs.',
        icon: 'bi bi-clipboard2-check',
        colorClass: 'ecb-web-training-process-orange'
      },
      {
        id: 2,
        stepNumber: 2,
        title: 'Book Your Session',
        description: 'Choose a date, time and location.',
        icon: 'bi bi-calendar3',
        colorClass: 'ecb-web-training-process-blue'
      },
      {
        id: 3,
        stepNumber: 3,
        title: 'Attend Training',
        description: 'Learn from experienced trainers with hands-on practice.',
        icon: 'bi bi-people',
        colorClass: 'ecb-web-training-process-green'
      },
      {
        id: 4,
        stepNumber: 4,
        title: 'Assessment',
        description: 'Complete assessment to demonstrate your competence.',
        icon: 'bi bi-search',
        colorClass: 'ecb-web-training-process-purple'
      },
      {
        id: 5,
        stepNumber: 5,
        title: 'Get Certified',
        description: 'Receive your nationally recognised certificate.',
        icon: 'bi bi-award-fill',
        colorClass: 'ecb-web-training-process-orange'
      }
    ];
  }
  /* End Training Process Static List Section */


  /* Start Training Benefit Static List Section */
  getTrainingBenefits(): void {
    this.trainingBenefits = [
      {
        id: 1,
        title: 'RTO No: 41369 – Nationally Recognised Training'
      },
      {
        id: 2,
        title: 'Industry experienced and qualified trainers'
      },
      {
        id: 3,
        title: 'Flexible training options – Face to Face & Onsite'
      },
      {
        id: 4,
        title: 'Modern facilities and up-to-date equipment'
      },
      {
        id: 5,
        title: 'Supportive and engaging learning environment'
      },
      {
        id: 6,
        title: 'Courses tailored for individuals and businesses'
      }
    ];
  }
  /* End Training Benefit Static List Section */


  /* Start Student Testimonial Static List Section */
  getStudentTestimonials(): void {
    this.studentTestimonials = [
      {
        id: 1,
        studentName: 'Sarah J.',
        courseName: 'First Aid Course',
        message:
          'Great training experience! The trainer was very knowledgeable and made the course easy to understand.',
        rating: 5,
        image: 'assets/images/training/student-sarah.JPG'
      },
      {
        id: 2,
        studentName: 'Michael T.',
        courseName: 'CPR Course',
        message:
          'Very practical and hands-on. I feel much more confident in my CPR skills now.',
        rating: 5,
        image: 'assets/images/training/student-michael.JPG'
      },
      {
        id: 3,
        studentName: 'James L.',
        courseName: 'White Card Course',
        message:
          'The White Card course was well structured and the online option was very convenient.',
        rating: 5,
        image: 'assets/images/training/student-james.JPG'
      },
      {
        id: 4,
        studentName: 'Emma R.',
        courseName: 'Childcare Course',
        message:
          'The trainer explained everything clearly and provided excellent practical examples.',
        rating: 5,
        image: 'assets/images/training/student-emma.JPG'
      },
      {
        id: 5,
        studentName: 'Daniel W.',
        courseName: 'Safety Training',
        message:
          'A professional and informative course that helped improve my workplace safety knowledge.',
        rating: 5,
        image: 'assets/images/training/student-daniel.JPG'
      }
    ];
  }
  /* End Student Testimonial Static List Section */


  /* Start Displayed Testimonial List Function Section */
  getDisplayedTestimonials(): EcbStudentTestimonial[] {
    const startIndex: number =
      this.selectedTestimonialPage * this.testimonialItemsPerPage;

    const endIndex: number =
      startIndex + this.testimonialItemsPerPage;

    return this.studentTestimonials.slice(startIndex, endIndex);
  }
  /* End Displayed Testimonial List Function Section */


  /* Start Testimonial Page Count Function Section */
  getTestimonialPageCount(): number[] {
    const pageCount: number = Math.ceil(
      this.studentTestimonials.length / this.testimonialItemsPerPage
    );

    return Array.from(
      {
        length: pageCount
      },
      (value: unknown, index: number) => index
    );
  }
  /* End Testimonial Page Count Function Section */


  /* Start Testimonial Page Selection Function Section */
  selectTestimonialPage(pageIndex: number): void {
    this.selectedTestimonialPage = pageIndex;
  }
  /* End Testimonial Page Selection Function Section */


  /* Start Rating Star List Function Section */
  getRatingStars(rating: number): number[] {
    return Array.from(
      {
        length: rating
      },
      (value: unknown, index: number) => index
    );
  }
  /* End Rating Star List Function Section */


  /* Start Track By Function Section */
  trackById(index: number, item: { id: number }): number {
    return item.id;
  }
  /* End Track By Function Section */

}
